import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { downloadPDF, fixDate } from "../../helpers/helperFunctions";
import { icons, lang_list, vatPrefixes } from "../../helpers/constants";
import * as request from "../../services/FetchService";
import toast from "react-hot-toast";
import PaymentsModal from "../Modals/PaymentsModal";
import { FLAVOR, CUST } from "../../helpers/constants";

const Payments = ({ receipts, setOuterType }) => {
	const { t } = useTranslation();
	const countrySelectRef = useRef(null);
	const [companyInfoEnabled, setCompanyInfoEnabled] = useState(false);
	const [sendEmailEnabled, setSendEmailEnabled] = useState(false);
	const [companyInfo, setCompanyInfo] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [type, setType] = useState();
	const [selectedLanguage, setSelectedLanguage] = useState();

	useEffect(() => {
		getBillingInfo();
	}, []);

	const getBillingInfo = async () => {
		const response = await request.get("account/receipt/billingprofile");
		const billing_data = response?.data?.response;
		setCompanyInfo(billing_data);
		setSendEmailEnabled(billing_data?.auto_send_receipt ?? false);
		setSelectedLanguage(billing_data?.country ?? "");
		setCompanyInfoEnabled(billing_data?.show_company_on_receipt ?? false);
	};

	const generatePDF = async (invoice_id, date) => {
		const response = await request.generatePDFget(`payments/dataplan/receipts/${invoice_id}/pdf`);
		if (response.status === 404) {
			const msg = response?.data?.message;
			toast.error(msg ?? t("toast.error"));
			return;
		}
		const blob = response.data; // Already a Blob
		const url = window.URL.createObjectURL(blob);

		const dateFixed = fixDate(date, true);
		const link = document.createElement("a");
		const customer = CUST ?? "dataplan"
		link.href = url;
		link.target = "_blank";
		link.download = `${customer}_receipt_${dateFixed ?? ""}.pdf`;

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		window.URL.revokeObjectURL(url);
	};

	const saveData = (data) => {
		request.post("", "account/receipt/billingprofile/update", data).then((response) => {
			if (response.data.successful) {
				if (!("country" in data) && !("auto_send_receipt" in data) && !("show_company_on_receipt" in data)) {
					setShowModal(!showModal);
				}
				getBillingInfo();
				toast.success(t("toast.saved"));
			} else {
				const msg = response?.data?.message ?? t("toast.failed");
				toast.error(msg);
			}
		});
	};

	const toggleCompanyInfo = (state) => {
		const data = { show_company_on_receipt: state };
		saveData(data);
	};

	const toggleSendEmail = (state) => {
		const data = { auto_send_receipt: state };
		saveData(data);
		//setSendEmailEnabled(state)
	};

	const handleSelectCountry = (country) => {
		const data = { country: country };
		saveData(data);
	};

	const openCountrySelect = () => {
		const el = countrySelectRef.current;
		if (!el) return;
		el.focus();
		if (typeof el.showPicker === "function") {
			el.showPicker(); // modern browsers
		} else {
			el.click(); // fallback
		}
	};

	const openModal = (type) => {
		setType(type);
		setShowModal(!showModal);
	};

	const items = [
		{
			key: "company_name",
			label: t("subscription.company"),
			value: companyInfo?.company_name ?? "",
			icon: icons.dots_vertical,
			function: openModal,
		},
		{
			key: "org_nr",
			label: t("subscription.org_nr"),
			value: companyInfo?.organization_number ?? "",
			icon: icons.dots_vertical,
			function: openModal,
		},
		{
			key: "vat_nr",
			label: t("subscription.vat_nr"),
			value: companyInfo?.vat_number ?? "",
			icon: icons.dots_vertical,
			function: openModal,
		},
		{
			key: "country",
			label: t("subscription.country"),
			spec: (
				<select ref={countrySelectRef} onChange={(event) => handleSelectCountry(event.target.value)} value={selectedLanguage} className="country-select">
					{/* <option value="">{t("settings.select")}</option> */}
					<option value="">{""}</option>

					{vatPrefixes.map((lang, index) => {
						return (
							<option key={index} value={lang.value}>
								{lang.label}
							</option>
						);
					})}
				</select>
			),
			icon: icons.chevron_down,
			function: openCountrySelect,
		},
		// {
		// 	key: "reference",
		// 	label: t("subscription.reference"),
		// 	value: "",
		// 	icon: icons.dots_vertical,
		// 	destructive: true,
		// 	function: openModal
		// }
	];

	const emailItem = [
		{
			key: "email",
			label: t("subscription.recipient"),
			value: companyInfo?.invoice_email ?? "",
			icon: icons.dots_vertical,
			function: openModal,
		},
	];

	return (
		<>
			<div className="d-flex flex-column align-items-center">
				<div className={`d-flex flex-column w-100 dataplan-selection`}>
					{/* Company INFO */}
					{FLAVOR === "tracking" && (
						<div className="mb-3">
							<p className="bold mb-1">{t("subscription.company_info")}</p>
							<div className="d-flex flex-column">
								<div className={`settings-radius`}>
									<div className={`p-3 d-flex justify-content-between align-items-center bg-white border-settings`}>
										<span className={`body`}>{t("subscription.company_info_sub")}</span>
									</div>
									<div className={`px-3 py-2 d-flex justify-content-between bg-white align-items-center`}>
										<span className="body">{t("subscription.enable")}</span>
										<div className="form-check form-switch">
											<input className="form-check-input" type="checkbox" onChange={() => toggleCompanyInfo(!companyInfoEnabled)} role="switch" id="flexSwitchCheckDefault" checked={companyInfoEnabled} />
										</div>
									</div>
								</div>
							</div>
							{companyInfoEnabled && (
								<>
									<div className="d-flex flex-column settings-radius mt-2">
										{items.map((item, index) => (
											<div onClick={() => item?.function(item.key)} key={index} className={`cursor-pointer p-3 d-flex justify-content-between align-items-center bg-white ${index < items.length - 1 ? "border-settings" : ""}`}>
												<span className={`body primary ${item.destructive ? "red" : ""}`}>{item.label}</span>
												<div className="d-flex align-items-center">
													{item?.spec ? item?.spec : <p className="bold">{item?.value}</p>}
													<img className="ms-3 size_15 grey-blue-icon" src={item.icon} alt={""} />
												</div>
											</div>
										))}
									</div>
									{companyInfo?.permissions?.company_fields_complete && (
										<>
											<p className="bold mb-1 mt-3">{t("subscription.receipts_delivery")}</p>
											<div className="d-flex flex-column">
												<div className={`settings-radius`}>
													<div className={`px-3 py-2 d-flex justify-content-between bg-white align-items-center border-settings`}>
														<span className="body">{t("subscription.send_to_email")}</span>
														<div className="form-check form-switch">
															<input className="form-check-input" type="checkbox" disabled={!companyInfo?.permissions?.can_enable_auto_send ?? false} onChange={() => toggleSendEmail(!sendEmailEnabled)} role="switch" id="flexSwitchCheckDefault" checked={sendEmailEnabled} />
														</div>
													</div>
													{emailItem.map((item, index) => (
														<div onClick={() => item?.function(item.key)} key={index} className={`cursor-pointer p-3 d-flex justify-content-between align-items-center bg-white ${index < items.length - 1 ? "border-settings" : ""}`}>
															<span className={`body primary ${item.destructive ? "red" : ""}`}>{item.label}</span>
															<div className="d-flex align-items-center">
																{item?.spec ? item?.spec : <p className="bold">{item?.value}</p>}
																<img className="ms-3 size_15 grey-blue-icon" src={item.icon} alt={""} />
															</div>
														</div>
													))}
												</div>
											</div>
										</>
									)}
								</>
							)}
						</div>
					)}

					{/* RECEIPTS */}
					<p className="bold mb-1">{t("subscription.receipts")}</p>
					<div className="d-flex flex-column">
						<div className={`settings-radius`}>
							{receipts?.map((receipt, index) => {
								return (
									<div
										key={index}
										className={`p-3 w-100 d-flex justify-content-between align-items-center bg-white ${index < receipts?.length - 1 ? "border-settings" : ""} cursor-pointer`}
										onClick={() => (receipt?.invoice_pdf ? downloadPDF(receipt.invoice_pdf, false) : generatePDF(receipt?.invoice_id, receipt?.paydate))}
									>
										<div className="d-flex flex-column">
											<p className="body bold">{fixDate(receipt.paydate, true) ?? "LOL"}</p>
											{/* <p className="">
												{t("subscription.payment_method")}: <span className="bold">{receipt?.payment ?? "LOL"}</span>
											</p> */}
										</div>
										<img src={icons.download} className="size_15" alt="download_icon" />
									</div>
								);
							})}
						</div>
						{receipts?.length === 0 && (
							<div className="d-flex flex-column settings-radius bg-white p-3 w-100">
								<span className="regular">{t("subscription.no_receipts")}</span>
							</div>
						)}
					</div>
				</div>
			</div>
			<PaymentsModal show={showModal} type={type} data={companyInfo} onSave={(data) => saveData(data)} onClose={() => setShowModal(!showModal)} />
		</>
	);
};

export default Payments;
